import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useStateMachine } from 'little-state-machine';
import { useForm } from 'react-hook-form';

import updateFormAction from 'utils/updateFormAction';
import { FormDataProps } from 'utils/types';
import { BREAKPOINTS } from 'constants/common';

import PaymentForm from './PaymentForm';
import OrderSummary from './OrderSummary';
import { getPlanEstimate } from 'v6/api/signup';
import { ChargeBeeEstimate } from './types';
import { PreviousNav } from 'v6/Components/PreviousNav';
import { ContactSection } from '../Homepage/Actions';
import { bugsnagNotify } from 'utils/error';

const PaymentDetails: React.FC = () => {
  const [coupon, setCoupon] = useState('');
  const [couponAdded, setCouponAdded] = useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isSubmittingForm, setIsSubmittingForm] = useState<boolean>(false);
  const { state } = useStateMachine({ updateFormAction });
  const defaultValuesFromStorage = state as FormDataProps;
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [onSubmit, setOnSubmit] = useState<any>();
  const [estimate, setEstimate] = useState<ChargeBeeEstimate | null>(null);
  const [chargebeeError, setChargebeeError] = useState<string>('');

  const [gettingEstimate, setGettingEstimate] = useState(false);

  const { selectedPlanId, user, currency, numberAccounts, billingPeriod } =
    defaultValuesFromStorage;

  useEffect(() => {
    const data = {
      _id: user._id,
      currency,
      plan: selectedPlanId,
      billingPeriod,
      numberAccounts,
      coupon_ids: coupon ? [coupon] : undefined,
    };

    const getEstimate = async () => {
      try {
        setCouponAdded(false);
        setGettingEstimate(true);

        const response = await getPlanEstimate(data);
        setGettingEstimate(false);

        if (response.status === 'error' && response.error) {
          setChargebeeError(response.error.error_msg);
          setCoupon('');

          return;
        }

        if (coupon) {
          setCouponAdded(true);
        }

        setEstimate(response);
      } catch (e) {
        bugsnagNotify(e as Error, 'Error getting estimate');
        console.error(e);
      } finally {
        setGettingEstimate(false);
      }
    };

    getEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coupon]);

  const {
    control,
    handleSubmit,
    formState: { errors, isValid: isValidForm },
    getValues,
  } = useForm<FormDataProps>({
    mode: 'onChange',
  });

  return (
    <PaymentDetailsWrapper>
      <FormWrapper>
        {!isSubmittingForm && !isSubscribed && (
          <PreviousNavWrap>
            <PreviousNav url="/plan" style={{ position: 'relative' }} />
          </PreviousNavWrap>
        )}
        <FormWrapperInner>
          <h1>Please enter payment details</h1>
          <CentredP>
            Your card will not be charged if you cancel any time during your 7 day free trial.
          </CentredP>
          <PaymentForm
            chargebeeError={chargebeeError}
            setChargebeeError={setChargebeeError}
            coupon={coupon}
            couponAdded={couponAdded}
            setCoupon={setCoupon}
            isSubscribed={isSubscribed}
            setIsSubscribed={setIsSubscribed}
            setIsSubmittingForm={setIsSubmittingForm}
            isSubmittingForm={isSubmittingForm}
            setIsFormValid={setIsFormValid}
            control={control}
            errors={errors}
            isValidForm={isValidForm}
            getValues={getValues}
            setOnSubmit={setOnSubmit}
          />
        </FormWrapperInner>
      </FormWrapper>

      <SelectedPlanWrapper>
        <OrderSummary
          isLoading={estimate ? gettingEstimate : true}
          estimate={estimate?.next_invoice_estimate || null}
          selectedPlanId={selectedPlanId}
          isSubscribed={isSubscribed}
          isSubmittingForm={isSubmittingForm}
          isFormValid={isFormValid}
          handleSubmit={handleSubmit(onSubmit)}
        />
      </SelectedPlanWrapper>
      <ContactSectionWrapMobile>
        <ContactSection />
      </ContactSectionWrapMobile>
    </PaymentDetailsWrapper>
  );
};

export default PaymentDetails;

const FormWrapper = styled.div`
  width: 100%;
  order: 0;
  height: calc(100vh - 132px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding-top: 32px;

  @media (max-width: ${BREAKPOINTS.tablet}px) {
    height: auto;
    padding: 16px;
    padding-top: 24px;
  }
`;
const CentredP = styled.p`
  text-align: center;
`;
const FormWrapperInner = styled.div`
  width: 420px;
  display: flex;
  flex-grow: 1;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  h1 {
    margin: 0;
    color: var(--Text-T01, #333);
    text-align: center;

    /* Web App/Heading 3 */
    font-family: Gotham SSm A, Gotham SSm B, sans-serif;
    font-size: 24px;
    font-style: normal;
    font-weight: 700;
    line-height: 32px; /* 133.333% */
  }

  p {
    color: var(--Text-T02, #454545);

    /* Web App/Paragraph 2 Regular */
    font-family: Inter;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 20px; /* 142.857% */
  }

  @media (max-width: ${BREAKPOINTS.tablet}px) {
    width: 100%;

    h1 {
      margin-top: 32px;
    }
  }
`;

const SelectedPlanWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  height: calc(100vh - 132px);
  background: var(--Primary-P25, #fcfaff);
  padding-top: 32px;

  @media (max-width: ${BREAKPOINTS.tablet}px) {
    height: auto;
    padding: 16px;
    padding-top: 24px;
  }
`;

const PaymentDetailsWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  flex-direction: row;
  flex-wrap: wrap;

  @media (min-width: ${BREAKPOINTS.tablet}px) {
    flex-direction: row;
    flex-wrap: wrap;
  }

  @media (min-width: ${BREAKPOINTS.maxTablet}px) {
    flex-wrap: nowrap;
    gap: 35px;
  }
`;

const PreviousNavWrap = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-start;
`;

const ContactSectionWrapMobile = styled.div`
  display: none;
  @media (max-width: ${BREAKPOINTS.tablet}px) {
    display: block;
    padding-left: 16px;
  }
`;
